import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "components/Graphs/Button";
import { motion, AnimatePresence } from "framer-motion";
import DGXComputeCard from "../Create/ComputeSelector/Advanced/DGX/DGXComputeCard";
import Chip from "../Create/chips";

// Sample data for the cards
const GPU_CARDS = [
  { id: 2, name: "H100" },
  { id: 3, name: "A100" },
  { id: 4, name: "L40S" },
  { id: 5, name: "A10G" },
  { id: 6, name: "T4" },
];

export default function GPUCardFan() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const handlePrevious = () => {
    setDirection(-1);
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + GPU_CARDS.length) % GPU_CARDS.length
    );
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % GPU_CARDS.length);
  };

  // Get the three visible cards with wrapping
  const getVisibleCards = () => {
    const visibleCards: { id: number; name: string; position: number }[] = [];

    // Previous card (wrap to the end if at the beginning)
    const prevIndex = (currentIndex - 1 + GPU_CARDS.length) % GPU_CARDS.length;
    visibleCards.push({ ...GPU_CARDS[prevIndex], position: -1 });

    // Current card
    visibleCards.push({ ...GPU_CARDS[currentIndex], position: 0 });

    // Next card (wrap to the beginning if at the end)
    const nextIndex = (currentIndex + 1) % GPU_CARDS.length;
    visibleCards.push({ ...GPU_CARDS[nextIndex], position: 1 });

    return visibleCards;
  };

  return (
    <div className="relative w-full max-w-[467px] h-[142px] flex items-center justify-center">
      {/* Left pagination indicator */}
      {/* <Button
        variant="secondary"
        className="absolute left-1 z-10 rounded-full h-6 w-6 p-0"
        onClick={handlePrevious}
      >
        <ChevronLeft className="h-3 w-3" />
      </Button> */}

      {/* Card fan container */}
      <div className="relative w-full max-w-[400px] h-full flex items-center justify-center">
        <AnimatePresence initial={false} mode="popLayout">
          {getVisibleCards().map((card) => {
            // Get the position (-1, 0, 1)
            const position = card.position;

            // Calculate the angle for the fan effect
            const angle = position * 10;

            // Calculate the distance from the center based on position
            const xOffset = position * 60;

            return (
              <motion.div
                key={card.id}
                initial={{
                  x: direction > 0 ? xOffset - 60 : xOffset + 60,
                  rotate: angle,
                  scale: 0.8,
                  opacity: 0,
                  zIndex: 10 - Math.abs(position),
                }}
                animate={{
                  x: xOffset,
                  rotate: angle,
                  scale: position === 0 ? 1 : 0.85,
                  opacity: 1,
                  zIndex: 10 - Math.abs(position),
                }}
                exit={{
                  x: direction > 0 ? xOffset + 60 : xOffset - 60,
                  rotate: angle,
                  scale: 0.8,
                  opacity: 0,
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
                className="absolute cursor-pointer"
                onMouseEnter={() => setHoveredCard(card.id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => {
                  if (position < 0) {
                    handlePrevious();
                  } else if (position > 0) {
                    handleNext();
                  }
                }}
              >
                <motion.div
                  animate={{
                    y: hoveredCard === card.id ? -10 : 0,
                    scale: hoveredCard === card.id ? 1.05 : 1,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 10,
                    mass: 1,
                  }}
                >
                  <Chip
                    selected={true}
                    chipName={card.name}
                    manufacturerName={"NVIDIA"}
                    onClick={() => {}}
                    className={"h-[100px] mr-3 flex-shrink-0"}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Right pagination indicator */}
      {/* <Button
        variant="secondary"
        className="absolute right-1 z-10 rounded-full h-6 w-6 p-0"
        onClick={handleNext}
      >
        <ChevronRight className="h-3 w-3" />
      </Button> */}

      {/* Pagination dots */}
      <div className="absolute bottom-1 flex space-x-1">
        {GPU_CARDS.map((_, index) => (
          <div
            key={index}
            className={`h-1.5 w-1.5 rounded-full ${
              index === currentIndex ? "bg-primary" : "bg-zinc-800"
            }`}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            style={{ cursor: "pointer" }}
          />
        ))}
      </div>
    </div>
  );
}
